/**
 * mapInstance — module-scoped MapLibre map singleton.
 *
 * Created as Phase 7 of the HI-2' refactor (audit synthesis 2026-05-24).
 * Previously this lived as a module-level `let mapInstance: MlMap | null`
 * in MapView.tsx alongside getMapInstance() and safeGetSource().
 *
 * Moving it here:
 *   - Removes the need for consumers to import from MapView (avoids
 *     pulling in the entire 2900-LOC component).
 *   - Makes the singleton's lifecycle explicit.
 *   - Allows other modules (TerrainController, OwnshipController, etc.)
 *     to call getMapInstance() without MapView import overhead.
 *
 * MapView.tsx imports from here and re-exports getMapInstance /
 * safeGetSource for backward compatibility with call sites that already
 * import from MapView.
 */

import type { Map as MlMap } from 'maplibre-gl';

let _mapInstance: MlMap | null = null;

/** Set the global map instance. Called by MapView on mount. */
export function setMapInstance(map: MlMap): void {
  _mapInstance = map;
}

/** Clear the global map instance. Called by MapView on unmount. */
export function clearMapInstance(map: MlMap): void {
  if (_mapInstance === map) _mapInstance = null;
}

/**
 * Returns the live MapLibre map instance, or null if not yet mounted.
 * Used by controllers and utility functions that need the map outside
 * React's component tree.
 */
export function getMapInstance(): MlMap | null {
  return _mapInstance;
}

/**
 * Defensively fetch a source from the map. Returns undefined if the
 * map has been torn down, lost its WebGL context, or hasn't finished
 * initializing its style.
 *
 * Background: a track-detail PWA crash showed
 * `Cannot read properties of undefined (reading 'getSource')` from
 * inside pushAirports/pushFixes/pushNavaids — the map object was still
 * in scope but `map.style` had been nulled after a WebGL context loss
 * on iOS. Wrapping in try/catch prevents the crash from propagating
 * through async .then() callbacks.
 */
export function safeGetSource<T = unknown>(
  map: MlMap | null | undefined,
  id: string,
): T | undefined {
  if (!map) return undefined;
  try {
    const fn = (map as unknown as { getSource?: (id: string) => unknown }).getSource;
    if (typeof fn !== 'function') return undefined;
    return fn.call(map, id) as T | undefined;
  } catch {
    return undefined;
  }
}
